import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight, FileText, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { SignaturePad } from "../components/SignaturePad";
import { buildServiceAgreement, planLabel, SERVICE_AGREEMENT_VERSION } from "../components/ServiceAgreement";
import { buildEasyReadServiceAgreement } from "../components/EasyReadAgreement";
import { API } from "../utils/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4a6e65b4-dfc4-4116-a52d-9af75b9ab8f4/artifacts/dhbjnlbf_btr%20logo.png";

/**
 * PublicSignAgreementPage — no auth required. Loads a Service Agreement draft
 * by token, lets a participant read it in Normal or Easy Read mode, and sign
 * it electronically (typed name + drawable signature).
 */
export default function PublicSignAgreementPage() {
  const { token } = useParams();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readMode, setReadMode] = useState("normal");   // "normal" | "easy"
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [signedResult, setSignedResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/public/sign-agreement/${token}`);
        if (r.ok) {
          const d = await r.json();
          setDraft(d);
          if (d.status === "signed") setSignedResult({ signed_at: d.signed_at });
        } else {
          const err = await r.json().catch(() => ({}));
          toast.error(err.detail || "This signing link is invalid or expired");
        }
      } catch {
        toast.error("Couldn't load the agreement");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    const expected = (draft?.participant_name || "").trim().toLowerCase();
    if (!signedName.trim() || signedName.trim().toLowerCase() !== expected) {
      return toast.error("Please type your full legal name exactly as shown");
    }
    if (!signatureDataUrl) {
      return toast.error("Please draw your signature in the box below");
    }
    if (!agreed) {
      return toast.error("Please tick the box to confirm you agree");
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/sign-agreement/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signed_name: signedName, agreed: true, signature_data_url: signatureDataUrl }),
      });
      const d = await r.json();
      if (r.ok) {
        setSignedResult(d);
        toast.success("Signed! You'll receive a copy by email.");
      } else {
        toast.error(d.detail || "Couldn't sign the agreement");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#87CEEB]" />
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Link not found</h2>
          <p className="text-sm text-slate-500">
            This signing link is invalid or has expired. Ask BTR Health to send you a new one.
          </p>
        </div>
      </div>
    );
  }

  if (signedResult) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-8 max-w-md text-center shadow"
          data-testid="sign-success"
        >
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
          <h2 className="text-xl font-semibold text-slate-800 mb-1">All signed!</h2>
          <p className="text-sm text-slate-500 mb-4">
            Thank you, {draft.participant_name}. BTR Health has your Service Agreement on file.
          </p>
          <p className="text-xs text-slate-400">
            Signed at {new Date(signedResult.signed_at || Date.now()).toLocaleString("en-AU")}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGO_URL} alt="BTR Health" className="h-10" />
          <ReadModeToggle mode={readMode} onChange={setReadMode} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">
            NDIS Service Agreement
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            For <strong className="text-slate-700">{draft.participant_name}</strong>
            {draft.ndis_number && <> · NDIS {draft.ndis_number}</>}
            {draft.ndis_plan_type && <> · {planLabel(draft.ndis_plan_type)}</>}
          </p>

          <AgreementBody
            draft={draft}
            readMode={readMode}
            onReadToBottom={() => setScrolledToEnd(true)}
          />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Sign your agreement</h3>

          <div className="flex items-start gap-2 mb-4">
            <Checkbox
              id="agreed"
              checked={agreed}
              onCheckedChange={setAgreed}
              disabled={!scrolledToEnd}
              data-testid="agreement-checkbox"
            />
            <label htmlFor="agreed" className="text-sm text-slate-700 leading-relaxed">
              I have read this Service Agreement and I agree to it.
              {!scrolledToEnd && (
                <span className="block text-xs text-amber-600 mt-1">
                  Please scroll to the bottom of the agreement above first.
                </span>
              )}
            </label>
          </div>

          <div className="mb-4">
            <Label className="text-xs">Type your full legal name to sign <span className="text-red-500">*</span></Label>
            <Input
              placeholder={`Type "${draft.participant_name}" exactly`}
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              className="h-12 mt-1 rounded-xl font-serif italic text-lg"
              data-testid="agreement-signature-input"
            />
          </div>

          <div className="mb-4">
            <Label className="text-xs">Draw your signature <span className="text-red-500">*</span></Label>
            <div className="mt-1">
              <SignaturePad onChange={setSignatureDataUrl} height={180} />
            </div>
          </div>

          <p className="text-[11px] text-slate-500 mb-3">
            Will be recorded as signed at {new Date().toLocaleString("en-AU")}. We record your IP and browser for audit.
          </p>

          <Button
            onClick={submit}
            disabled={submitting || !agreed}
            className="w-full h-12 bg-[#87CEEB] hover:bg-[#6BB8DB] text-white font-bold text-base rounded-xl"
            data-testid="agreement-submit-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>I agree &amp; sign <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      </main>
    </div>
  );
}

/**
 * Reusable Read Mode Toggle — used on public sign page, admin builder, and
 * the existing logged-in participant sign page.
 */
export function ReadModeToggle({ mode, onChange }) {
  return (
    <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold" data-testid="read-mode-toggle">
      <button
        type="button"
        onClick={() => onChange("normal")}
        className={`px-3 py-1.5 rounded-full transition-colors ${mode === "normal" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
        data-testid="read-mode-normal"
      >
        Normal
      </button>
      <button
        type="button"
        onClick={() => onChange("easy")}
        className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${mode === "easy" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
        data-testid="read-mode-easy"
      >
        <Sparkles className="w-3 h-3" />
        Easy Read
      </button>
    </div>
  );
}

/**
 * Shared agreement body — renders either Normal (buildServiceAgreement) or
 * Easy Read (buildEasyReadServiceAgreement). Emits `onReadToBottom` when the
 * user scrolls near the bottom.
 */
export function AgreementBody({ draft, readMode, onReadToBottom }) {
  const participantForBuilder = {
    name: draft.participant_name,
    email: draft.participant_email,
    phone: draft.participant_phone,
    dob: draft.date_of_birth,
    address: draft.address,
    ndis_number: draft.ndis_number,
    ndis_plan_type: draft.ndis_plan_type,
    plan_manager_name: draft.plan_manager_name,
    plan_manager_email: draft.plan_manager_email,
    emergency_contact_name: draft.emergency_contact_name,
    emergency_contact_phone: draft.emergency_contact_phone,
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - (el.scrollTop + el.clientHeight) < 40) {
      onReadToBottom?.();
    }
  };

  if (readMode === "easy") {
    const sections = buildEasyReadServiceAgreement({
      participant: participantForBuilder,
      servicesSummary: draft.services_summary,
      startDate: draft.start_date,
      endDate: draft.end_date,
    });
    return (
      <div
        onScroll={handleScroll}
        className="h-[420px] overflow-y-auto pr-3 border border-slate-200 rounded-xl p-5 bg-slate-50"
        data-testid="agreement-scroll-box"
      >
        {sections.map((s, i) => (
          <div key={i} className="mb-6 last:mb-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl" aria-hidden="true">{s.icon}</span>
              <h3 className="text-lg font-bold text-slate-800">{s.title}</h3>
            </div>
            <ul className="space-y-2 pl-1">
              {s.lines.map((line, j) => (
                <li key={j} className="text-base text-slate-700 leading-relaxed">
                  {line}
                </li>
              ))}
            </ul>
          </div>
        ))}
        <p className="text-xs text-slate-400 mt-6 text-center">
          Version {SERVICE_AGREEMENT_VERSION} — Easy Read
        </p>
      </div>
    );
  }

  const sections = buildServiceAgreement(participantForBuilder);
  return (
    <div
      onScroll={handleScroll}
      className="h-[420px] overflow-y-auto pr-3 border border-slate-200 rounded-xl p-5"
      data-testid="agreement-scroll-box"
    >
      {sections.map((c) => (
        <div key={c.heading} className="mb-4">
          <h3 className="text-sm font-bold text-slate-800 mb-1">{c.heading}</h3>
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{c.body}</p>
        </div>
      ))}
      <p className="text-xs text-slate-400 mt-6 text-center">Version {SERVICE_AGREEMENT_VERSION}</p>
    </div>
  );
}
